import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Tabs, 
  Tab,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material';
import * as Icons from '@mui/icons-material';
import { collection, getDocs, doc, setDoc, deleteDoc, query } from 'firebase/firestore';
import { db } from '../firebase.js';
import { useInventory } from '../contexts/InventoryContext.js';
import { useUser } from '../contexts/UserContext.js';
import IngredientsList from '../IngredientsList.js';

// The main page component for the Ingredients section
function IngredientsPage({ recipes }) {
  const [ingredientSets, setIngredientSets] = useState([]);
  const [activeSetId, setActiveSetId] = useState(null);
  const [activeSet, setActiveSet] = useState(null);
  const [isCreatingSet, setIsCreatingSet] = useState(false);
  const [newSetName, setNewSetName] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const { inventory, togglePossession } = useInventory();
  const { user } = useUser();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Load ingredient sets from Firebase
  const loadIngredientSets = async () => {
    if (!user) return;
    
    try {
      const setsRef = collection(db, `users/${user.uid}/ingredientSets`);
      const snapshot = await getDocs(setsRef);
      const sets = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setIngredientSets(sets);
      
      // Get active set from localStorage
      const savedActiveSetId = localStorage.getItem('activeIngredientSet');
      if (savedActiveSetId && sets.some(set => set.id === savedActiveSetId)) {
        setActiveSetId(savedActiveSetId);
        setActiveSet(sets.find(set => set.id === savedActiveSetId));
      }
    } catch (error) {
      console.error('Error loading ingredient sets:', error);
    }
  };

  // Toggle an ingredient in the active set
  const toggleIngredientInSet = async (ingredientId) => {
    if (!activeSetId || !activeSet) return;
    
    try {
      // Create a new set with the ingredient toggled
      const updatedIngredients = [...(activeSet.ingredients || [])];
      const index = updatedIngredients.findIndex(ing => ing === ingredientId);
      
      if (index === -1) {
        updatedIngredients.push(ingredientId);
      } else {
        updatedIngredients.splice(index, 1);
      }
      
      // Update the set in Firebase
      const setRef = doc(db, `users/${user.uid}/ingredientSets`, activeSetId);
      await setDoc(setRef, { 
        name: activeSet.name,
        ingredients: updatedIngredients 
      }, { merge: true });
      
      // Update local state
      const updatedSet = { ...activeSet, ingredients: updatedIngredients };
      setActiveSet(updatedSet);
      
      // Update the set in the ingredientSets array
      const updatedSets = ingredientSets.map(set => 
        set.id === activeSetId ? updatedSet : set
      );
      setIngredientSets(updatedSets);
    } catch (error) {
      console.error('Error toggling ingredient in set:', error);
    }
  };

  // Create a new ingredient set
  const createIngredientSet = async () => {
    if (!user || !newSetName.trim()) return;
    
    try {
      const setsRef = collection(db, `users/${user.uid}/ingredientSets`);
      const newSetRef = doc(setsRef);
      
      const newSet = {
        name: newSetName.trim(),
        ingredients: [],
        created: new Date()
      };
      
      await setDoc(newSetRef, newSet);
      
      // Add to local state
      const setWithId = {
        id: newSetRef.id,
        ...newSet
      };
      
      setIngredientSets([...ingredientSets, setWithId]);
      setActiveSetId(newSetRef.id);
      setActiveSet(setWithId);
      
      // Save active set to localStorage
      localStorage.setItem('activeIngredientSet', newSetRef.id);
      
      // Reset UI state
      setNewSetName('');
      setIsCreatingSet(false);
    } catch (error) {
      console.error('Error creating ingredient set:', error);
    }
  };

  // Rename the active ingredient set
  const renameIngredientSet = async () => {
    if (!activeSetId || !renameValue.trim()) return;
    
    try {
      const setRef = doc(db, `users/${user.uid}/ingredientSets`, activeSetId);
      await setDoc(setRef, { name: renameValue.trim() }, { merge: true });
      
      // Update local state
      const updatedSet = { ...activeSet, name: renameValue.trim() };
      setActiveSet(updatedSet);
      
      // Update the set in the ingredientSets array
      const updatedSets = ingredientSets.map(set => 
        set.id === activeSetId ? updatedSet : set
      );
      setIngredientSets(updatedSets);
      
      // Reset UI state
      setRenameValue('');
      setIsRenaming(false);
    } catch (error) {
      console.error('Error renaming ingredient set:', error);
    }
  };

  // Delete the active ingredient set
  const deleteIngredientSet = async () => {
    if (!activeSetId) return;
    
    try {
      const setRef = doc(db, `users/${user.uid}/ingredientSets`, activeSetId);
      await deleteDoc(setRef);
      
      // Update local state
      const updatedSets = ingredientSets.filter(set => set.id !== activeSetId);
      setIngredientSets(updatedSets);
      
      // Reset active set
      setActiveSetId(null);
      setActiveSet(null);
      localStorage.removeItem('activeIngredientSet');
      
      // Reset UI state
      setIsDeleting(false);
    } catch (error) {
      console.error('Error deleting ingredient set:', error);
    }
  };

  // Load ingredient sets on component mount
  useEffect(() => {
    loadIngredientSets();
  }, [user]);

  // Update activeSet when activeSetId changes
  useEffect(() => {
    if (activeSetId) {
      setActiveSet(ingredientSets.find(set => set.id === activeSetId));
      localStorage.setItem('activeIngredientSet', activeSetId);
    } else {
      setActiveSet(null);
      localStorage.removeItem('activeIngredientSet');
    }
  }, [activeSetId, ingredientSets]);

  // Prepare inventory for display
  const prepareInventoryForDisplay = () => {
    if (!activeSetId) {
      // No set selected, use regular inventory
      return inventory.filter(ing => 
        ing.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    } else {
      // Use the active set
      return inventory
        .filter(ing => ing.name.toLowerCase().includes(searchQuery.toLowerCase()))
        .map(ing => ({
          ...ing,
          possessed: activeSet?.ingredients?.includes(ing.id) || false
        }));
    }
  };

  const displayInventory = prepareInventoryForDisplay();

  // Custom toggle handler that delegates to the appropriate function
  const handleToggle = (ingredientId) => {
    if (!activeSetId) {
      // No set selected, use regular toggle
      togglePossession(ingredientId);
    } else {
      // Toggle the ingredient in the active set
      toggleIngredientInSet(ingredientId);
    }
  };

  return (
    <Box sx={{ p: 2, pb: 8 }}>
      {/* Page Header */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" component="h1" gutterBottom>
          Ingredients
        </Typography>
      </Box>

      {/* Ingredient Set Selection and Search */}
      <Box sx={{ mb: 2 }}>
        {/* Ingredient Set Selector */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'flex-end', 
          gap: 1, 
          mb: 2
        }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
              Current Ingredient Set
            </Typography>
            <Select
              fullWidth
              size="small"
              value={activeSetId || ''}
              onChange={(e) => setActiveSetId(e.target.value)}
              displayEmpty
            >
              <MenuItem value="">📋 Main Inventory</MenuItem>
              {ingredientSets.map(set => (
                <MenuItem key={set.id} value={set.id}>{set.name}</MenuItem>
              ))}
            </Select>
          </Box>
          <Button 
            variant="outlined" 
            startIcon={<Icons.Add />}
            onClick={() => setIsCreatingSet(true)}
            size="small"
          >
            New Set
          </Button>
        </Box>

        {/* Set Management Buttons */}
        {activeSetId && (
          <Box sx={{ 
            display: 'flex', 
            gap: 1,
            height: 36,
            mb: 2
          }}>
            <Button 
              size="small"
              startIcon={<Icons.Edit />}
              onClick={() => {
                setRenameValue(activeSet?.name || '');
                setIsRenaming(true);
              }}
            >
              Rename
            </Button>
            <Button 
              size="small"
              color="error"
              startIcon={<Icons.Delete />}
              onClick={() => setIsDeleting(true)}
            >
              Delete
            </Button>
          </Box>
        )}

        {/* Search Box */}
        <TextField
          fullWidth
          placeholder="Search ingredients..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: <Icons.Search color="action" sx={{ mr: 1 }} />,
            endAdornment: searchQuery ? (
              <IconButton
                size="small"
                onClick={() => setSearchQuery('')}
              >
                <Icons.Clear fontSize="small" />
              </IconButton>
            ) : null,
          }}
          size="small"
        />
      </Box>
      
      {/* Status indicator for which set is being used */}
      {activeSetId ? (
        <Box sx={{ mb: 2 }}>
          <Typography 
            variant="body2" 
            color="primary"
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 0.5,
              fontStyle: 'italic'
            }}
          >
            <Icons.Info fontSize="small" />
            {`Editing ingredients in "${activeSet?.name}" set`}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ mb: 2 }}>
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 0.5,
              fontStyle: 'italic'
            }}
          >
            <Icons.Info fontSize="small" />
            Editing your main inventory
          </Typography>
        </Box>
      )}

      {/* Ingredients List */}
      <Box>
        <IngredientsList
          inventory={displayInventory}
          togglePossession={handleToggle}
          recipes={recipes}
          ingredients={inventory}
        />
      </Box>

      {/* Create New Set Dialog */}
      <Dialog open={isCreatingSet} onClose={() => setIsCreatingSet(false)}>
        <DialogTitle>Create New Ingredient Set</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Enter a name for your new ingredient set.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Set Name"
            fullWidth
            value={newSetName}
            onChange={(e) => setNewSetName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsCreatingSet(false)}>Cancel</Button>
          <Button 
            onClick={createIngredientSet}
            color="primary"
            disabled={!newSetName.trim()}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename Set Dialog */}
      <Dialog open={isRenaming} onClose={() => setIsRenaming(false)}>
        <DialogTitle>Rename Ingredient Set</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="New Name"
            fullWidth
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsRenaming(false)}>Cancel</Button>
          <Button 
            onClick={renameIngredientSet}
            color="primary"
            disabled={!renameValue.trim()}
          >
            Rename
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Set Confirmation Dialog */}
      <Dialog open={isDeleting} onClose={() => setIsDeleting(false)}>
        <DialogTitle>Delete Ingredient Set</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the ingredient set "{activeSet?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleting(false)}>Cancel</Button>
          <Button 
            onClick={deleteIngredientSet}
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default IngredientsPage; 